import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import { ToWords } from "to-words";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import RecommendedChecks from "../components/RecommendedChecks";
import "../styles/SingleCheck.css";
import { useCompany } from "../hooks/useCompany";
import { useUserProfile } from "../hooks/useUserProfile";

const API_URL = process.env.REACT_APP_API_URL;

const formatMoney = (number) => {
  const numberFloat = parseFloat(number || 0);
  return numberFloat.toLocaleString("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toWordsTool = new ToWords({
  localeCode: "en-US",
  converterOptions: {
    currency: true,
    ignoreDecimal: false,
    ignoreZeroCurrency: false,
    doNotAddOnly: true,
  },
});

const toWords = (denom) => {
  try {
    return toWordsTool.convert(Number(denom));
  } catch (e) {
    return `${e}`;
  }
};

const getWeekRange = (offset = 0) => {
  const today = new Date();
  today.setDate(today.getDate() + offset * 7);
  const dayOfWeek = today.getDay();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (6 - dayOfWeek));
  endOfWeek.setHours(23, 59, 59, 999);
  return {
    startDate: startOfWeek.toISOString().split("T")[0],
    endDate: endOfWeek.toISOString().split("T")[0],
  };
};

// Helper to split an array into chunks of a given size
const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, (i + 1) * size)
  );

// Helper function to reformat a date string from YYYY-MM-DD to MM-DD-YYYY
const formatDateMMDDYYYY = (dateString) => {
  const parts = dateString.split("-");
  if (parts.length !== 3) return dateString;
  return `${parts[1]}-${parts[2]}-${parts[0]}`;
};

export const EmployeeCheck = ({ check }) => {
  const lineRef = useRef(null);
  return (
    <div className="wrapper" style={{ position: "relative", marginTop: 0 }}>
      <div className="check-box" id="check-box">
        <div style={{ position: "relative" }} className="check-box-print">
          <div
            className="account-holder-name"
            style={{ position: "absolute", top: "40px", left: "60px" }}
          >
            {check.accountHolderName}
          </div>
          <div
            className="account-holder-address"
            style={{ position: "absolute", top: "70px", left: "60px" }}
          >
            {check.accountHolderAddress}
            <br />
            {check.accountHolderCity}, {check.accountHolderState} {check.accountHolderZip}
          </div>
          <div
            className="check-number-human"
            style={{ position: "absolute", top: "40px", left: "1060px" }}
          >
            {check.checkNumber}
          </div>
          <div
            className="date-data"
            style={{ position: "absolute", top: "80px", left: "850px" }}
          >
            {check.date}
          </div>
          <div
            className="date"
            style={{ position: "absolute", top: "90px", left: "760px" }}
          >
            Date: _____________________
          </div>
          <div
            className="amount-box"
            style={{ position: "absolute", top: "175px", left: "950px" }}
          />
          <div
            className="amount-data"
            style={{ position: "absolute", top: "182px", left: "970px" }}
          >
            {formatMoney(check.amount)}
          </div>
          <div
            className="pay-to-data"
            style={{ position: "absolute", top: "180px", left: "180px" }}
          >
            {check.payTo}
          </div>
          <div
            className="pay-to"
            style={{ position: "absolute", top: "170px", left: "60px" }}
          >
            Pay to the <br />
            Order of <span className="payto-line"></span>
          </div>
          <div
            className="amount-line-data"
            ref={lineRef}
            style={{ position: "absolute", top: "240px", left: "100px" }}
          >
            *** {toWords(check.amount)} ***
          </div>
          <div
            className="amount-line"
            style={{ position: "absolute", top: "250px", left: "60px" }}
          >
            <span className="dollar-line"></span>
          </div>
          <div
            className="bank-name"
            style={{ position: "absolute", top: "300px", left: "60px" }}
          >
            {check.bankName}
          </div>
          {/* The fractional routing number, under the bank's name (backend/bank_fraction.py). */}
          {check.bankFraction && (
            <div className="bank-fraction" style={{ position: "absolute", top: "334px", left: "60px" }}>
              {check.bankFraction}
            </div>
          )}
          <div
            className="memo-data"
            style={{ position: "absolute", top: "367px", left: "120px" }}
          >
            {check.memo}
          </div>
          <div
            className="memo"
            style={{ position: "absolute", top: "390px", left: "60px" }}
          >
            Memo: ____________________________________
          </div>
          <div
            className="signature-data"
            style={{ position: "absolute", top: "366px", left: "770px" }}
          >
            {check.signature}
          </div>
          <div
            className="signature"
            style={{ position: "absolute", top: "390px", left: "750px" }}
          >
            ____________________________________________
          </div>
          <div
            className="banking"
            style={{ position: "absolute", top: "420px", left: "80px" }}
          >
            <div className="routing" style={{ display: "inline", marginLeft: "100px" }}>
              a{check.routingNumber}a
            </div>
            <div className="bank-account" style={{ display: "inline", marginLeft: "100px" }}>
              {check.bankAccountNumber}c
            </div>
            <div className="check-number" style={{ display: "inline", marginLeft: "100px" }}>
              {check.checkNumber}
            </div>
          </div>
        </div>
      </div>
      {/* Summary section similar to SingleCheck.js */}
      <div className="print-summary" style={{ display: "none" }}>
        <table>
          <tbody>
            <tr>
              <td><strong>Account Holder Name:</strong></td>
              <td>{check.accountHolderName}</td>
            </tr>
            <tr>
              <td><strong>Address:</strong></td>
              <td>
                {check.accountHolderAddress}
                {check.accountHolderCity && `, ${check.accountHolderCity}`}
                {check.accountHolderState && `, ${check.accountHolderState}`}
                {check.accountHolderZip && ` ${check.accountHolderZip}`}
              </td>
            </tr>
            <tr>
              <td><strong>Check Number:</strong></td>
              <td>{check.checkNumber}</td>
            </tr>
            <tr>
              <td><strong>Date:</strong></td>
              <td>{check.date}</td>
            </tr>
            <tr>
              <td><strong>Bank Name:</strong></td>
              <td>{check.bankName}</td>
            </tr>
            <tr>
              <td><strong>Amount:</strong></td>
              <td>{formatMoney(check.amount)}</td>
            </tr>
            <tr>
              <td><strong>Pay To:</strong></td>
              <td>{check.payTo}</td>
            </tr>
            <tr>
              <td><strong>Memo:</strong></td>
              <td>{check.memo}</td>
            </tr>
            <tr>
              <td><strong>Signature:</strong></td>
              <td>{check.signature}</td>
            </tr>
            <tr>
              <td><strong>Routing Number:</strong></td>
              <td>{check.routingNumber}</td>
            </tr>
            <tr>
              <td><strong>Bank Account Number:</strong></td>
              <td>{check.bankAccountNumber}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PrintWeeklyChecks = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { activeCompany, isDemo, refetch } = useCompany();
  const { profile } = useUserProfile();

  const accountHolderName    = activeCompany?.name             || "";
  const accountHolderAddress = activeCompany?.address          || "";
  const accountHolderCity    = activeCompany?.city             || "";
  const accountHolderState   = activeCompany?.state            || "";
  const accountHolderZip     = activeCompany?.zip              || "";
  const bankName             = activeCompany?.bank_name        || "";
  const bankFraction         = activeCompany?.bank_fraction    || "";
  const routingNumber        = activeCompany?.routing_number   || "";
  const bankAccountNumber    = activeCompany?.account_number   || "";

  const missingInfo =
    !accountHolderName ||
    !accountHolderAddress ||
    !accountHolderCity ||
    !accountHolderState ||
    !accountHolderZip ||
    !bankName ||
    !routingNumber ||
    !bankAccountNumber;

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(() => getWeekRange(0).startDate);
  const [endDate, setEndDate]     = useState(() => getWeekRange(0).endDate);
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [checksToPrint, setChecksToPrint] = useState([]);
  const [printingInProgress, setPrintingInProgress] = useState(false);
  const [leaveSignatureBlank, setLeaveSignatureBlank] = useState(false);
  const [printThreeUp, setPrintThreeUp] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [sort, setSort] = useState({ key: "first_name", dir: "asc" });

  // kept for the memo label on generated checks
  const weekRange = useMemo(() => ({ startDate, endDate }), [startDate, endDate]);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    try {
      const token = auth.user?.access_token;
      const response = await fetch(`${API_URL}/subscription/status`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
      }
    } catch (err) {
      console.error("Error fetching subscription status:", err);
    }
  }, [auth]);

  const fetchData = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.access_token || !activeCompany) { setLoading(false); return; }
    setLoading(true);
    try {
      const token = auth.user.access_token;
      const companyId = activeCompany.company_id;
      const res = await fetch(
        `${API_URL}/payroll_entries/weekly_summary?start_date=${startDate}&end_date=${endDate}&company_id=${companyId}`,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (!res.ok) throw new Error(`Failed to fetch payroll summary (Status: ${res.status})`);
      const data = await res.json();
      setEmployees(data);
      const initialSelection = {};
      data.forEach((emp) => { initialSelection[emp.employee_id] = false; });
      setSelectedEmployees(initialSelection);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth, startDate, endDate, activeCompany]);

  const hasActiveSubscription = () => {
    const status = subscriptionData?.subscriptionStatus?.toLowerCase();
    const isAdmin = subscriptionData?.is_admin === true;
    return isAdmin || status === "active" || status === "trialing" || status === "admin_bypass" || isDemo;
  };

  const showSubscriptionPrompt = () => {
    Swal.fire({
      icon: 'info',
      title: t("subscription_required"),
      html: `
        <p>${t("sub_required_print_weekly_msg")}</p>
      `,
      showCancelButton: true,
      confirmButtonText: t("view_plans"),
      cancelButtonText: t("maybe_later"),
      confirmButtonColor: '#3b82f6'
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = '/ManageSubscription';
      }
    });
  };

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchData();
  }, [fetchSubscriptionStatus, fetchData]);

  useEffect(() => {
    const preloadFonts = async () => {
      try {
        const bankingFont = new FontFace('banking', 'url(/assets/fonts/micrenc.ttf)');
        await bankingFont.load();
        document.fonts.add(bankingFont);
        await document.fonts.load("16px 'Caveat'");
      } catch (error) {
        console.error("Error preloading fonts", error);
      }
    };
    preloadFonts();
  }, [fetchData, startDate, auth]);

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  const getWeeklyPay = (emp) => emp.total_pay || 0;

  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedEmployees = useMemo(() => {
    const { key, dir } = sort;
    return [...employees].sort((a, b) => {
      if (key === "weekly_pay") {
        return dir === "asc" ? (a.total_pay || 0) - (b.total_pay || 0) : (b.total_pay || 0) - (a.total_pay || 0);
      }
      if (key === "wage") {
        return dir === "asc" ? Number(a.wage) - Number(b.wage) : Number(b.wage) - Number(a.wage);
      }
      const av = (a[key] || "").toLowerCase();
      const bv = (b[key] || "").toLowerCase();
      return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [employees, sort]);

  const sortIcon = (col) => (
    <span className={`ml-1 text-xs ${sort.key === col ? "text-gray-700" : "text-gray-300"}`}>
      {sort.key === col ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const toggleSelectAll = () => {
    const selectableIds = employees
      .filter((emp) => getWeeklyPay(emp) > 0)
      .map((emp) => emp.employee_id);
    const allSelected = selectableIds.every((id) => selectedEmployees[id]);
    const newSelection = { ...selectedEmployees };
    selectableIds.forEach((id) => { newSelection[id] = !allSelected; });
    setSelectedEmployees(newSelection);
  };

  const fetchLatestCheckNumber = async () => {
    if (!auth.isAuthenticated) return "0001";
    try {
      const companyParam = activeCompany ? `?company_id=${activeCompany.company_id}` : "";
      const res = await fetch(`${API_URL}/checks/latest_number${companyParam}`, {
        headers: { Authorization: `Bearer ${auth.user?.access_token}` },
      });
      if (!res.ok) return "0001";
      const data = await res.json();
      return data.check_number;
    } catch {
      return "0001";
    }
  };

  const handlePrintChecks = async () => {
    if (printingInProgress) return;
    setPrintingInProgress(true);

    const selectedIds = Object.keys(selectedEmployees).filter((id) => selectedEmployees[id]);
    if (selectedIds.length === 0) { setPrintingInProgress(false); return; }

    let currentCheckNumber = await fetchLatestCheckNumber();
    const signature = leaveSignatureBlank ? "" : (
      [
        profile?.first_name,
        profile?.middle_initial ? `${profile.middle_initial}.` : null,
        profile?.last_name,
      ].filter(Boolean).join(" ") || auth.user?.profile?.name || ""
    );

    const checksToPost = [];
    const selectedEmps = employees.filter((emp) => selectedIds.includes(emp.employee_id));
    for (const emp of selectedEmps) {
      const weeklyPay = emp.total_pay || 0;
      if (!weeklyPay || weeklyPay <= 0) continue;
      checksToPost.push({
        user_id: auth.user?.profile.sub,
        company_id: activeCompany?.company_id || null,
        accountHolderName,
        accountHolderAddress,
        accountHolderCity,
        accountHolderState,
        accountHolderZip,
        checkNumber: currentCheckNumber,
        date: new Date().toLocaleDateString("en-US"),
        bankName,
        bankFraction: bankFraction || null,
        amount: weeklyPay,
        payTo: `${emp.first_name} ${emp.last_name}`,
        memo: `Week of ${formatDateMMDDYYYY(weekRange.startDate)} Payroll`,
        signature,
        routingNumber,
        bankAccountNumber,
      });
      let nextNum = parseInt(currentCheckNumber, 10) + 1;
      if (nextNum > 9999) nextNum = 0;
      currentCheckNumber = nextNum.toString().padStart(currentCheckNumber.length, "0");
    }

    if (checksToPost.length === 0) { setPrintingInProgress(false); return; }

    try {
      const res = await fetch(`${API_URL}/checks/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${auth.user?.access_token}` },
        body: JSON.stringify(checksToPost),
      });
      if (res.status === 409 || res.status === 503) {
        // Numbers are shared with the company's Olene bank account. The server already renumbered
        // once after a conflict; this is a second conflict, or Olene couldn't confirm. Nothing saved.
        const detail = (await res.json().catch(() => ({})))?.detail || {};
        const titles = {
          check_number_used: t("check_number_used_title"),
          choose_bank: t("choose_bank_for_checks"),
          bank_changed: t("bank_changed_msg"),
        };
        Swal.fire({
          icon: "error",
          title: titles[detail.code] || t("check_numbers_unconfirmed"),
          text: detail.code === "check_number_used"
            ? t("check_numbers_used_msg", { numbers: (detail.numbers || []).join(", ") }) : undefined,
        });
        if (detail.code === "bank_changed") refetch();
        setPrintingInProgress(false);
        return;
      }
      if (!res.ok) throw new Error(await res.text());

      // Print the numbers the server saved: it renumbers a batch whose numbers another app used.
      const saved = await res.json().catch(() => []);
      setChecksToPrint(checksToPost.map((c, i) => ({ ...c, checkNumber: saved[i]?.checkNumber || c.checkNumber })));
      setTimeout(() => {
        window.print();
        setChecksToPrint([]);
        setPrintingInProgress(false);
      }, 500);
    } catch (err) {
      console.error("Error posting checks:", err);
      Swal.fire({ icon: "error", title: "Error", text: `Could not save checks: ${err.message}` });
      setPrintingInProgress(false);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut} disableNavigation={printingInProgress}>
      <div className="flex-1">
        <div>
          {/* Screen UI */}
          <div className="no-print p-4 bg-gray-100 min-h-screen">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6 max-w-5xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-800 mb-4">
            {t("print_weekly_checks_title")}
          </h1>

          <RecommendedChecks className="mb-6" />

          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600 whitespace-nowrap">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600 whitespace-nowrap">To</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <button
              className="text-xs text-blue-500 hover:underline"
              onClick={() => {
                const { startDate: s, endDate: e } = getWeekRange(0);
                setStartDate(s);
                setEndDate(e);
              }}
            >
              Reset to current week
            </button>
          </div>
          {loading ? (
            <p>{t("loading_data")}</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-200 text-gray-700">
                    <th className="px-4 py-2 border">
                      <input
                        type="checkbox"
                        checked={
                          employees.some((emp) => getWeeklyPay(emp) > 0) &&
                          employees
                            .filter((emp) => getWeeklyPay(emp) > 0)
                            .every((emp) => selectedEmployees[emp.employee_id])
                        }
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("first_name")}>{t("col_employee")}{sortIcon("first_name")}</th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("weekly_pay")}>{t("col_hours_worked")}{sortIcon("weekly_pay")}</th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("wage")}>{t("col_hourly_salary")}{sortIcon("wage")}</th>
                    <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("weekly_pay")}>{t("col_weekly_pay")}{sortIcon("weekly_pay")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEmployees.map((emp) => {
                    const weeklyPay = emp.total_pay || 0;
                    const hasNoPay = weeklyPay <= 0;
                    return (
                      <tr
                        key={emp.employee_id}
                        className={hasNoPay ? "bg-gray-50 opacity-50" : "hover:bg-gray-50"}
                      >
                        <td className="px-4 py-2 border text-center">
                          <input
                            type="checkbox"
                            checked={selectedEmployees[emp.employee_id] || false}
                            onChange={() => toggleEmployeeSelection(emp.employee_id)}
                            disabled={hasNoPay}
                            className={hasNoPay ? "cursor-not-allowed" : ""}
                          />
                        </td>
                        <td className="px-4 py-2 border font-medium">
                          {emp.first_name} {emp.last_name}
                          {hasNoPay && <span className="ml-2 text-xs font-normal text-gray-400">(no hours)</span>}
                        </td>
                        <td className="px-4 py-2 border text-right tabular-nums text-gray-500">
                          {(emp.total_hours || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-2 border text-right tabular-nums text-gray-500">
                          {emp.is_salary
                            ? `Salary/${emp.salary_period === "yearly" ? "yr" : emp.salary_period === "monthly" ? "mo" : "wk"}`
                            : `$${Number(emp.wage).toFixed(2)}/hr`}
                        </td>
                        <td className="px-4 py-2 border text-right tabular-nums font-medium">
                          ${weeklyPay.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {missingInfo && (
            <p className="text-red-500 mb-4">
              {t("missing_bank_info")}{" "}
              <a
                href="/CompanySettings"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {t("company_settings_link")}
              </a>{" "}
              {t("missing_bank_info_suffix")}
            </p>
          )}
          <div className="mt-6 flex justify-end items-center space-x-4">
            {/* Checkbox to toggle leaving signature blank */}
            <label className="mr-4 flex items-center">
              <input
                type="checkbox"
                checked={leaveSignatureBlank}
                onChange={(e) => setLeaveSignatureBlank(e.target.checked)}
                className="mr-2"
              />
              {t("leave_signature_blank")}
            </label>
            {/* Checkbox for 3-per-page printing */}
            <label className="mr-4 flex items-center">
              <input
                type="checkbox"
                checked={printThreeUp}
                onChange={(e) => setPrintThreeUp(e.target.checked)}
                className="mr-2"
              />
              {t("per_page_3")}
            </label>
            <button
              onClick={hasActiveSubscription() ? handlePrintChecks : showSubscriptionPrompt}
              disabled={
                printingInProgress ||
                Object.values(selectedEmployees).filter((val) => val).length === 0 ||
                missingInfo ||
                !hasActiveSubscription()
              }
              className={`px-4 py-2 rounded transition ${
                printingInProgress ||
                Object.values(selectedEmployees).filter((val) => val).length === 0 ||
                missingInfo ||
                !hasActiveSubscription()
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
              title={!hasActiveSubscription() ? 'Subscription required to print checks' : ''}
            >
              {printingInProgress ? t("generating") : hasActiveSubscription() ? t("print_checks") : t("subscribe_to_print_weekly")}
            </button>
            <Link to="/">
              <button className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
                {t("back_to_dashboard_btn")}
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Print Layout */}
      <div className="print-only" style={{ display: "none" }}>
        {printThreeUp
          ? chunk(checksToPrint, 3).map((pageChecks, pageIndex) => (
              <div key={pageIndex} className="check-3up-page">
                {pageChecks.map((chk, i) => (
                  <EmployeeCheck key={i} check={chk} />
                ))}
              </div>
            ))
          : checksToPrint.map((chk, index) => (
              <EmployeeCheck key={index} check={chk} />
            ))
        }
      </div>

      {/* Print CSS Overrides */}
      <style>{`
        @media print {
          body, html {
            margin: 0 !important;
            padding: 0 !important;
            width: 100%;
            height: 100%;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          .wrapper {
            position: relative !important;
            margin-bottom: 20px;
            page-break-after: always;
            width: 100%;
          }
          .check-box {
            background: white !important;
            border: none !important;
            position: relative !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 1200px !important;
            height: 750px !important;
            overflow: visible !important;
          }
          .print-summary {
            display: block !important;
            margin: 20px auto;
            text-align: center;
            font-size: 20px;
            width: 100%;
            page-break-inside: avoid;
          }
          .print-summary table {
            margin: 0 auto;
            text-align: left;
            border-collapse: collapse;
          }
          .print-summary td {
            padding: 4px 8px;
          }
          @page {
            margin: 0 !important;
          }
          /* 3-per-page: break only BETWEEN groups, not after the last one */
          .check-3up-page + .check-3up-page {
            page-break-before: always;
          }
          /* Keep all 3 checks in a group on one page, no breaks within */
          .check-3up-page {
            page-break-inside: avoid;
          }
          .check-3up-page .wrapper {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            margin-bottom: 0 !important;
          }
          /* Hide per-check summaries in 3-up mode */
          .check-3up-page .print-summary {
            display: none !important;
          }
          /* Each check section = 3.5" of the 10.5" printable area (494px at browser print scale) */
          .check-3up-page .check-box {
            height: 494px !important;
          }
        }
      `}</style>
        </div>
      </div>
      </Sidebar>
    </div>
  );
};

export default PrintWeeklyChecks;
